<template>
  <div class="mt-4">
    <div class="flex">
      <Button
        :title="$t('widget-form-monthly-or-donation.monthly')"
        class="w-1/2 rounded-br-none rounded-tr-none text-center normal-case lg:!py-2.5"
        :class-name-text="
          twMerge(
            'text-base lg:text-lg font-normal lg:font-normal',
            useDarkMode ? 'text-white' : '',
          )
        "
        :type="urlSelected === url[1] ? 'outline' : undefined"
        @click="urlSelected = url[0]"
      />
      <Button
        :title="$t('widget-form-monthly-or-donation.one-time')"
        :type="urlSelected === url[0] ? 'outline' : undefined"
        class="w-1/2 rounded-bl-none rounded-tl-none normal-case lg:!py-2.5"
        :class-name-text="
          twMerge(
            'text-base lg:text-lg font-normal lg:font-normal',
            useDarkMode ? 'text-white' : '',
          )
        "
        @click="urlSelected = url[1]"
      />
    </div>
    <p class="my-4 hidden font-sans text-base lg:block">
      {{ $t('choose-a-sum-to-contribute') }}
    </p>
    <div class="mt-6 flex space-x-2 lg:mt-0 lg:space-x-4">
      <Button
        v-for="(sum, index) in sums"
        :key="index"
        :title="`${sum.value} ${sum.currency}`"
        class="flex-1 py-2 lowercase lg:!py-2 lg:leading-none"
        :class-name-text="twMerge('text-sm lg:text-base', useDarkMode ? 'text-white' : '')"
        :type="sumSelected === sum.value ? undefined : 'outline'"
        @click="handleClickSum(sum.value)"
      />
      <Button
        :title="$t('other')"
        class="flex-1 py-2 lowercase lg:!py-2 lg:leading-none"
        :class-name-text="twMerge('text-sm lg:text-base', useDarkMode ? 'text-white' : '')"
        :type="otherSum ? undefined : 'outline'"
        @click="handleClickOther"
      />
    </div>

    <Input
      v-show="otherSum"
      v-model="sumSelected"
      type="number"
      :v$="v$.sumSelected"
      :min="minSum"
      :label="$t('your-amount-to-donate')"
      :style-label="useDarkMode ? 'text-white' : ''"
      style-input="border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] text-blue text-sm lg:text-base font-bold !pr-10 w-40"
      :currency="currency"
      class="mt-6 items-start"
      :right="otherRightString"
    />
  </div>
  <Button
    :title="$t('next-step')"
    color="pink"
    class="mt-8 w-full lg:max-w-[200px]"
    :disabled="v$.sumSelected.$invalid"
    @click="goToNextStep"
  />
</template>

<script lang="ts" setup>
/** Monthly + single donation split form */
import { Input, Button } from 'refresh-ui'
import { useI18n } from '#imports'
import { useVuelidate } from '@vuelidate/core'
import { helpers, minValue } from '@vuelidate/validators'
import { withQuery } from 'ufo'
import { twMerge } from 'tailwind-merge'

const { t } = useI18n()
const config = useRuntimeConfig()

const settings = inject<string[]>('settings', [])
const useDarkMode = computed(() => settings?.includes('dark_theme') ?? false)

const is1882 = ref(config.public.siteName.includes('1882'))
const isMonthly = computed(() => urlSelected.value === url[0])
const currency = is1882.value ? '€' : 'kr'

const otherRightString = `<span class='absolute right-4 bottom-3 lg:bottom-4 text-sm font-bold text-blue lg:text-base'>${currency}</span>`

const url = [
  t('component-form.split.membership_monthly.url'),
  t('component-form.split.donation.url'),
]

const minSum = computed(() => {
  if (is1882.value) return 5 // 5 euro
  return isMonthly.value ? 50 : 25 // 50 kr for monthly, 25 kr for single donation
})

const sums = is1882.value
  ? [
      { value: 50, currency },
      { value: 25, currency },
      { value: 10, currency },
    ]
  : [
      { value: 200, currency },
      { value: 100, currency },
      { value: 75, currency },
    ]

const urlSelected = ref(url[0])
const sumSelected = ref(sums[1].value)
const otherSum = ref(false)

const handleClickSum = (sum: number) => {
  sumSelected.value = sum
  otherSum.value = false
  v$.value.$touch()
}

const handleClickOther = () => {
  v$.value.$touch()
  otherSum.value = true
  // sumSelected.value = 0 // Lets not suggest that a donation of 0 is a great idea
}

const rules = computed(() => {
  return {
    sumSelected: {
      minValue: helpers.withMessage(
        t('sum.min-sum.error', { sum: minSum.value, currency: t('currency') }),
        minValue(minSum.value),
      ),
    },
  }
})

const v$ = useVuelidate(rules, { sumSelected })

const goToNextStep = async () => {
  v$.value.$touch()
  v$.value.$validate()
  if (v$.value.$invalid) {
    return
  }
  await navigateTo(
    withQuery(urlSelected.value, {
      sum: sumSelected.value,
      step: urlSelected.value === url[0] ? 'present' : 'details',
    }),
    { external: true },
  )
}

const props = defineProps({
  formComponent: Object,
})
</script>
